/**
 * Argus — UpdatesWindow: floating global admin window pentru Auto-update management.
 *
 * Bloc msi-deployment 2026-05-06: refactor /admin/updates page → floating window.
 *
 * UI characteristics (per user spec):
 *   - Custom titlebar cu: Minimize + Close (NU maximize, NU restore)
 *   - NU resize (Rnd enableResizing=false)
 *   - Drag prin titlebar
 *   - Render în .argus-dashboard-canvas la zOrder gestionat de adminModalsStore
 *   - Integrare în WindowsTaskbar (secțiune Admin tools — citește direct din store)
 *   - Genie effect minimize → translate la taskbar button (consistent cu FloatingWindow)
 */

import { useCallback, useEffect, useState } from 'react';
import { Rnd, type RndDragCallback } from 'react-rnd';
import { Box, IconButton, Paper, Tooltip, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MinimizeIcon from '@mui/icons-material/Minimize';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { useAdminModalsStore } from '../../store/adminModalsStore';
import { getTaskbarButtonRect } from '../../lib/taskbarButtonRefs';
import { UpdatesContent } from '../../pages/admin/UpdatesPage';

const TITLEBAR_CLASS = 'argus-floating-titlebar-updates';
const TITLEBAR_HEIGHT = 36;
export const ADMIN_UPDATES_WINDOW_KEY = '__admin_updates__';

export function UpdatesWindow(): JSX.Element | null {
  const theme = useTheme();
  const updates = useAdminModalsStore((s) => s.updates);
  const closeUpdates = useAdminModalsStore((s) => s.closeUpdates);
  const minimizeUpdates = useAdminModalsStore((s) => s.minimizeUpdates);
  const bringToFront = useAdminModalsStore((s) => s.bringUpdatesToFront);
  const setPosition = useAdminModalsStore((s) => s.setUpdatesPosition);

  const [minimizeTransform, setMinimizeTransform] = useState<string | null>(null);

  // Genie effect minimize → taskbar button
  useEffect(() => {
    if (!updates.minimized) {
      setMinimizeTransform(null);
      return;
    }
    const raf = window.requestAnimationFrame(() => {
      const buttonRect = getTaskbarButtonRect(ADMIN_UPDATES_WINDOW_KEY);
      const canvas = document.querySelector('.argus-dashboard-canvas') as HTMLElement | null;
      const canvasOffsetTop = canvas?.getBoundingClientRect().top ?? 56;
      const canvasOffsetLeft = canvas?.getBoundingClientRect().left ?? 0;
      const winCenterX = canvasOffsetLeft + updates.x + updates.width / 2;
      const winCenterY = canvasOffsetTop + updates.y + updates.height / 2;
      if (buttonRect) {
        const btnX = buttonRect.left + buttonRect.width / 2;
        const btnY = buttonRect.top + buttonRect.height / 2;
        setMinimizeTransform(`translate(${btnX - winCenterX}px, ${btnY - winCenterY}px) scale(0.05)`);
      } else {
        setMinimizeTransform('scale(0.05)');
      }
    });
    return () => window.cancelAnimationFrame(raf);
  }, [updates.minimized, updates.x, updates.y, updates.width, updates.height]);

  const handleDragStop: RndDragCallback = useCallback(
    (_event, data) => setPosition(data.x, data.y),
    [setPosition]
  );

  if (!updates.open) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
      }}
    >
      <Box sx={{ position: 'relative', width: '100%', height: '100%', '& > *': { pointerEvents: 'auto' } }}>
        <Rnd
          size={{ width: updates.width, height: updates.height }}
          position={{ x: updates.x, y: updates.y }}
          bounds=".argus-dashboard-canvas"
          dragHandleClassName={TITLEBAR_CLASS}
          enableResizing={false}
          disableDragging={updates.minimized}
          onDragStop={handleDragStop}
          onMouseDown={() => {
            if (!updates.minimized) bringToFront();
          }}
          style={{
            zIndex: 100 + updates.zOrder,
            pointerEvents: updates.minimized ? 'none' : 'auto',
          }}
        >
          <Paper
            elevation={6}
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              border: 1,
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              transformOrigin: '50% 50%',
              transform: minimizeTransform ?? 'none',
              opacity: updates.minimized ? 0 : 1,
              pointerEvents: updates.minimized ? 'none' : 'auto',
              transition: 'transform 220ms cubic-bezier(0.55, 0.085, 0.68, 0.53), opacity 200ms ease-out',
            }}
          >
            {/* Titlebar */}
            <Box
              className={TITLEBAR_CLASS}
              sx={{
                height: TITLEBAR_HEIGHT,
                display: 'flex',
                alignItems: 'center',
                px: 1.25,
                backgroundColor: theme.palette.mode === 'dark' ? '#0F1419' : '#E5E7EB',
                borderBottom: 1,
                borderColor: 'divider',
                cursor: 'move',
                userSelect: 'none',
                gap: 1,
              }}
            >
              <SystemUpdateAltIcon sx={{ fontSize: 18, color: '#0EA5E9' }} />
              <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 600, fontSize: 13, color: 'text.primary' }} noWrap>
                Updates Management — Agent Auto-update
              </Typography>
              <Tooltip title="Minimize">
                <IconButton
                  size="small"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    bringToFront();
                    minimizeUpdates();
                  }}
                  sx={{ p: 0.5 }}
                  aria-label="Minimize window"
                >
                  <MinimizeIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  closeUpdates();
                }}
                sx={{ p: 0.5, '&:hover': { backgroundColor: 'error.dark', color: 'common.white' } }}
                aria-label="Close window"
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ flexGrow: 1, overflow: 'hidden', p: 1.5 }}>
              <UpdatesContent />
            </Box>
          </Paper>
        </Rnd>
      </Box>
    </Box>
  );
}
